import { useState, useEffect, useRef } from 'react';
import { writeOrgFile, deleteOrgFile, listOrgCollection, readOrgFile } from '../../storage/FileSystemAdapter';
import { appendChangeLog, isRecordImmutable } from '../../utils/changeLog';
import { callWorker } from '../../utils/workerClient';
import { SEVERITY_LEVELS, INCIDENT_STATUSES } from '../../utils/enums';
import PeopleSelector from '../shared/PeopleSelector';
import AISuggestButton from '../shared/AISuggestButton';
import TagInput from '../shared/TagInput';
import EntitySelector from '../shared/EntitySelector';
import ConfirmDialog from '../shared/ConfirmDialog';
import SaveErrorDialog from '../shared/SaveErrorDialog';
import { BTN_PRIMARY, BTN_SECONDARY, BTN_DESTRUCTIVE_OUTLINE, TEXT_MUTED, TEXT_DISABLED, TEXT_DEFAULT, BORDER_DEFAULT, TEXT_HEADING, BG_SURFACE, BRAND_PRIMARY, BRAND_PRIMARY_DARK, ERROR_TEXT, ERROR_BG, ERROR_BORDER, STATUS_GOOD, STATUS_WARN, STATUS_INFO, STATUS_NEUTRAL, FIELD_ERROR_BORDER, UNSAVED_AMBER, SURFACE_WHITE, FONT_SECTION_TITLE, FONT_BODY, FONT_BODY_STRONG, FONT_LABEL, FONT_SMALL, FONT_TINY, FONT_BUTTON } from '../../theme';

const aId = (id) => id ? `A.${id}` : id;

// ── CONSTANTS ─────────────────────────────────────────────────────────────────
const CATEGORIES = ['Malware','Ransomware','Phishing','Unauthorized access','Data breach','Availability','DDoS','Insider threat','Lost/stolen device','Third-party breach','Policy violation','Physical','Social engineering','Other'];
const TIMEZONES  = ['UTC','Europe/London','Europe/Madrid','Europe/Paris','Europe/Berlin','Europe/Amsterdam','Atlantic/Canary','America/New_York','America/Chicago','America/Denver','America/Los_Angeles','America/Mexico_City','America/Bogota','America/Lima','America/Santiago','America/Buenos_Aires','America/Sao_Paulo','America/Caracas'];
const SEVERITIES = SEVERITY_LEVELS;
const STATUSES   = INCIDENT_STATUSES;
const COMM_TYPES = ['Internal','Customer','Regulator','Law enforcement','Other'];

// ── I18N ──────────────────────────────────────────────────────────────────────
const T = {
  es: {
    titleCreate:          'Nuevo incidente',
    titleEdit:            'Editar incidente',
    titleView:            'Ver incidente',
    s1:                   'Identificación',
    s2:                   'Personas',
    s3:                   'Clasificación',
    s4:                   'Alcance afectado',
    s5:                   'Respuesta',
    s6:                   'Cronología',
    s7:                   'Comunicaciones',
    s8:                   'Post-incidente',
    s9:                   'Notificaciones',
    s10:                  'Relaciones',
    s11:                  'Ciclo de vida',
    fTitle:               'Título',
    fServiceDeskRef:      'Ref. del service desk',
    fDescription:         'Descripción',
    fDetectedAt:          'Fecha y hora de detección',
    fReportedBy:          'Reportado por',
    fIncidentCoordinator: 'Coordinador del incidente',
    fCategory:            'Categoría',
    fCategoryOther:       'Especificar otra categoría',
    fTimezone:            'Zona horaria',
    fSeverity:            'Severidad',
    fAffectedAssets:      'Activos afectados',
    fAffectedServices:    'Servicios afectados',
    fContainmentActions:  'Acciones de contención',
    fEradicationActions:  'Acciones de erradicación',
    fRecoveryActions:     'Acciones de recuperación',
    fTimeline:            'Cronología del incidente',
    fTimelineDatetime:    'Fecha/hora',
    fTimelineDescription: 'Descripción',
    fAddTimelineEntry:    '+ Añadir entrada',
    fCommunicationsLog:   'Registro de comunicaciones',
    fCommDatetime:        'Fecha/hora',
    fCommType:            'Tipo',
    fCommDescription:     'Descripción',
    fAddCommEntry:        '+ Añadir comunicación',
    fRootCause:           'Causa raíz',
    fLessonsLearned:      'Lecciones aprendidas',
    fControlImprovements: 'Mejoras de controles',
    fGdprNotifiable:      'Notificable RGPD',
    fNis2Notifiable:      'Notificable NIS2',
    fNotificationDetails: 'Detalles de notificación',
    s9Gdpr:               'RGPD',
    s9Nis2:               'NIS2',
    fGdprNotificationRequired:        'Notificación al regulador RGPD requerida',
    fGdprNotifiedAt:                  'Fecha y hora de notificación al regulador (límite 72 h)',
    fDataSubjectNotificationRequired: 'Comunicación a interesados requerida',
    fDataSubjectsNotifiedAt:          'Fecha y hora de comunicación a interesados',
    fNis2Applicable:                  'NIS2 aplicable',
    fNis2EarlyWarningAt:              'Alerta temprana NIS2 (límite 24 h)',
    fNis2NotificationAt:              'Notificación NIS2 (límite 72 h)',
    fNis2FinalReportAt:               'Informe final NIS2 (límite 30 días)',
    fNotifiedAuthorityIds:            'Autoridades notificadas',
    ttNotifiedAuthorityIds:           'Autoridades a las que se notificó este incidente',
    searchAuthority:                  'Buscar autoridad…',
    authorityInactiveSuffix:          ' (Inactiva)',
    noAuthoritiesHint:               'No hay autoridades registradas. Crea las autoridades en Registros → Autoridades para poder vincularlas.',
    fPersonalDataInvolved:    'Incidente con datos personales',
    fAffectedRecordsCount:    'Nº de registros afectados',
    fRegulatoryReportable:    'Reguladores a los que se notifica',
    fLessonsLearnedReviewDate:'Fecha de revisión de lecciones aprendidas',
    fControlIds:          'Controles del Anexo A',
    fEvidenceIds:         'Evidencias vinculadas',
    fLinkedNonconformityIds: 'No conformidades vinculadas',
    regulatorPlaceholder:     'Añadir regulador (p.ej. AEPD, INCIBE) y pulsar Enter…',
    recordsPlaceholder:       'p.ej. 1500',
    fCiaImpact:           'Impacto en propiedades de seguridad',
    fConfidentialityImpact: 'Confidencialidad (C)',
    fIntegrityImpact:     'Integridad (I)',
    fAvailabilityImpact:  'Disponibilidad (A)',
    fReportedAt:          'Fecha y hora de notificación',
    fResolvedAt:          'Fecha y hora de resolución',
    fStatus:              'Estado',
    fClosedAt:            'Fecha de cierre',
    nonconformityPlaceholder: 'Añadir ID de no conformidad y pulsar Enter…',
    optional:             '(opcional)',
    btnSave:              'Guardar',
    btnCancel:            'Cancelar',
    btnDelete:            'Eliminar incidente',
    btnConfirmDelete:     '¿Confirmar eliminación?',
    btnCancelDelete:      'No, cancelar',
    saving:               'Guardando…',
    saveOk:               '✓ Guardado',
    errorRequired:        'Campo obligatorio',
    errorSave:            'Error al guardar. Inténtalo de nuevo.',
    errorDelete:          'Error al eliminar.',
    required:             '*',
    requiredLegend:       '* Campos obligatorios',
    frozenBanner:         '🔒 Este registro es de solo lectura. Para modificarlo, reabre desde Ajustes → Reabrir registros cerrados.',
    generating:           'Generando…',
    yes:                  'Sí',
    no:                   'No',
    unknown:              'Desconocido',
    searchAsset:          'Buscar activo…',
    searchControl:        'Buscar control…',
    evidencePlaceholder:  'Añadir ID de evidencia y pulsar Enter…',
    servicePlaceholder:   'Añadir servicio y pulsar Enter…',
    actionPlaceholder:    'Añadir acción y pulsar Enter…',
    improvePlaceholder:   'Añadir mejora y pulsar Enter…',
    notSet:               'No establecido',
    noAssets:             'Sin activos afectados',
    noControls:           'Sin controles vinculados',
    searchSupplier:       'Buscar proveedor…',
    searchNonconformity:  'Buscar no conformidad…',
    searchLegalRegister:  'Buscar obligación legal…',
    fLinkedSupplierIds:      'Proveedores vinculados',
    fLinkedLegalRegisterIds: 'Obligaciones legales activadas',
    aiError:              'No se pudo obtener sugerencia. Inténtalo de nuevo.',
    aiErrorOverloaded:    'Servidores ocupados, inténtalo de nuevo en un momento.',
    aiConsentRequired:    'Activa el procesamiento con IA en Configuración para usar esta función.',
    unsavedLabel:         'Cambios sin guardar',
    severityLabels: {
      'Critical': 'Crítico',
      'High':     'Alto',
      'Medium':   'Moderado',
      'Low':      'Bajo',
    },
    statusLabels: {
      'Open':          'Abierto',
      'Investigating': 'Investigando',
      'Contained':     'Contenido',
      'Resolved':      'Resuelto',
      'Closed':        'Cerrado',
    },
    categoryLabels: {
      'Malware':             'Malware',
      'Ransomware':          'Ransomware',
      'Phishing':            'Phishing',
      'Unauthorized access': 'Acceso no autorizado',
      'Data breach':         'Brecha de datos',
      'Availability':        'Disponibilidad',
      'DDoS':                'DDoS',
      'Insider threat':      'Amenaza interna',
      'Lost/stolen device':  'Dispositivo perdido o robado',
      'Third-party breach':  'Brecha de terceros',
      'Policy violation':    'Violación de políticas',
      'Physical':            'Físico',
      'Social engineering':  'Ingeniería social',
      'Other':               'Otro',
    },
    commTypeLabels: {
      'Internal':        'Interna',
      'Customer':        'Cliente',
      'Regulator':       'Regulador',
      'Law enforcement': 'Autoridades',
      'Other':           'Otro',
    },
  },
  en: {
    titleCreate:          'New incident',
    titleEdit:            'Edit incident',
    titleView:            'View incident',
    s1:                   'Identification',
    s2:                   'People',
    s3:                   'Classification',
    s4:                   'Affected scope',
    s5:                   'Response',
    s6:                   'Timeline',
    s7:                   'Communications',
    s8:                   'Post-incident',
    s9:                   'Notifications',
    s10:                  'Relationships',
    s11:                  'Lifecycle',
    fTitle:               'Title',
    fServiceDeskRef:      'Service desk ref.',
    fDescription:         'Description',
    fDetectedAt:          'Detection date and time',
    fReportedBy:          'Reported by',
    fIncidentCoordinator: 'Incident coordinator',
    fCategory:            'Category',
    fCategoryOther:       'Specify other category',
    fTimezone:            'Timezone',
    fSeverity:            'Severity',
    fAffectedAssets:      'Affected assets',
    fAffectedServices:    'Affected services',
    fContainmentActions:  'Containment actions',
    fEradicationActions:  'Eradication actions',
    fRecoveryActions:     'Recovery actions',
    fTimeline:            'Incident timeline',
    fTimelineDatetime:    'Date/time',
    fTimelineDescription: 'Description',
    fAddTimelineEntry:    '+ Add entry',
    fCommunicationsLog:   'Communications log',
    fCommDatetime:        'Date/time',
    fCommType:            'Type',
    fCommDescription:     'Description',
    fAddCommEntry:        '+ Add communication',
    fRootCause:           'Root cause',
    fLessonsLearned:      'Lessons learned',
    fControlImprovements: 'Control improvements',
    fGdprNotifiable:      'GDPR notifiable',
    fNis2Notifiable:      'NIS2 notifiable',
    fNotificationDetails: 'Notification details',
    s9Gdpr:               'GDPR',
    s9Nis2:               'NIS2',
    fGdprNotificationRequired:        'GDPR regulator notification required',
    fGdprNotifiedAt:                  'Regulator notified at (72 h limit)',
    fDataSubjectNotificationRequired: 'Data subject notification required',
    fDataSubjectsNotifiedAt:          'Data subjects notified at',
    fNis2Applicable:                  'NIS2 applicable',
    fNis2EarlyWarningAt:              'NIS2 early warning at (24 h limit)',
    fNis2NotificationAt:              'NIS2 notification at (72 h limit)',
    fNis2FinalReportAt:               'NIS2 final report at (30 d limit)',
    fNotifiedAuthorityIds:            'Notified authorities',
    ttNotifiedAuthorityIds:           'Authorities notified about this incident',
    searchAuthority:                  'Search authority…',
    authorityInactiveSuffix:          ' (Inactive)',
    noAuthoritiesHint:               'No authorities recorded yet. Create authorities in Registers → Authorities to link them here.',
    fPersonalDataInvolved:    'Personal data involved',
    fAffectedRecordsCount:    'Affected records count',
    fRegulatoryReportable:    'Regulators to notify',
    fLessonsLearnedReviewDate:'Lessons learned review date',
    fControlIds:          'Annex A controls',
    fEvidenceIds:         'Linked evidence',
    fLinkedNonconformityIds: 'Linked nonconformities',
    regulatorPlaceholder:     'Add regulator (e.g. ICO, ENISA) and press Enter…',
    recordsPlaceholder:       'e.g. 1500',
    fCiaImpact:           'Security property impact',
    fConfidentialityImpact: 'Confidentiality (C)',
    fIntegrityImpact:     'Integrity (I)',
    fAvailabilityImpact:  'Availability (A)',
    fReportedAt:          'Reported date and time',
    fResolvedAt:          'Resolved date and time',
    fStatus:              'Status',
    fClosedAt:            'Closed at',
    nonconformityPlaceholder: 'Add nonconformity ID and press Enter…',
    optional:             '(optional)',
    btnSave:              'Save',
    btnCancel:            'Cancel',
    btnDelete:            'Delete incident',
    btnConfirmDelete:     'Confirm delete?',
    btnCancelDelete:      'No, cancel',
    saving:               'Saving…',
    saveOk:               '✓ Saved',
    errorRequired:        'Required',
    errorSave:            'Save failed. Please try again.',
    errorDelete:          'Delete failed.',
    required:             '*',
    requiredLegend:       '* Required fields',
    frozenBanner:         '🔒 This record is read-only. To modify it, reopen from Settings → Reopen closed records.',
    generating:           'Generating…',
    yes:                  'Yes',
    no:                   'No',
    unknown:              'Unknown',
    searchAsset:          'Search asset…',
    searchControl:        'Search control…',
    evidencePlaceholder:  'Add evidence ID and press Enter…',
    servicePlaceholder:   'Add service and press Enter…',
    actionPlaceholder:    'Add action and press Enter…',
    improvePlaceholder:   'Add improvement and press Enter…',
    notSet:               'Not set',
    noAssets:             'No affected assets',
    noControls:           'No linked controls',
    searchSupplier:       'Search supplier…',
    searchNonconformity:  'Search nonconformity…',
    searchLegalRegister:  'Search legal obligation…',
    fLinkedSupplierIds:      'Linked suppliers',
    fLinkedLegalRegisterIds: 'Activated legal obligations',
    aiError:              'Could not get suggestion. Please try again.',
    aiErrorOverloaded:    'Servers are busy, please try again in a moment.',
    aiConsentRequired:    'Enable AI processing in Settings to use this feature.',
    unsavedLabel:         'Unsaved changes',
    severityLabels: {
      'Critical': 'Critical',
      'High':     'High',
      'Medium':   'Medium',
      'Low':      'Low',
    },
    statusLabels: {
      'Open':          'Open',
      'Investigating': 'Investigating',
      'Contained':     'Contained',
      'Resolved':      'Resolved',
      'Closed':        'Closed',
    },
    categoryLabels: {
      'Malware':             'Malware',
      'Ransomware':          'Ransomware',
      'Phishing':            'Phishing',
      'Unauthorized access': 'Unauthorized access',
      'Data breach':         'Data breach',
      'Availability':        'Availability',
      'DDoS':                'DDoS',
      'Insider threat':      'Insider threat',
      'Lost/stolen device':  'Lost/stolen device',
      'Third-party breach':  'Third-party breach',
      'Policy violation':    'Policy violation',
      'Physical':            'Physical',
      'Social engineering':  'Social engineering',
      'Other':               'Other',
    },
    commTypeLabels: {
      'Internal':        'Internal',
      'Customer':        'Customer',
      'Regulator':       'Regulator',
      'Law enforcement': 'Law enforcement',
      'Other':           'Other',
    },
  },
};

// ── HELPERS ───────────────────────────────────────────────────────────────────
function yyyymmdd() { return new Date().toISOString().slice(0, 10).replace(/-/g, ''); }

async function generateIncidentIds(orgId, incidentPrefix) {
  let incidents = [];
  try { incidents = await listOrgCollection(orgId, 'incidents'); } catch { /* empty */ }

  const day       = yyyymmdd();
  const dayPrefix = `inc-${day}-`;
  let maxFileSeq  = 0;
  incidents.forEach((inc) => {
    if (inc.id && inc.id.startsWith(dayPrefix)) {
      const n = parseInt(inc.id.slice(dayPrefix.length), 10);
      if (!isNaN(n) && n > maxFileSeq) maxFileSeq = n;
    }
  });
  const fileId = `${dayPrefix}${String(maxFileSeq + 1).padStart(3, '0')}`;

  const year      = new Date().getFullYear();
  const refPrefix = `${incidentPrefix}-${year}-`;
  let maxRefSeq   = 0;
  incidents.forEach((inc) => {
    if (inc.ref && inc.ref.startsWith(refPrefix)) {
      const n = parseInt(inc.ref.slice(refPrefix.length), 10);
      if (!isNaN(n) && n > maxRefSeq) maxRefSeq = n;
    }
  });
  const ref = `${refPrefix}${String(maxRefSeq + 1).padStart(3, '0')}`;

  return { fileId, ref };
}

// ── INITIAL STATE ─────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  title: '', serviceDeskRef: '', description: '',
  detectedAt: '', reportedAt: '', resolvedAt: '',
  timezone: 'Europe/Madrid', reportedBy: '', incidentCoordinator: '',
  category: [], categoryOther: '', severity: 'Medium',
  ciaImpact: [],
  linkedAssetIds: [], affectedServices: [],
  timeline: [],
  containmentActions: [], eradicationActions: [], recoveryActions: [],
  communicationsLog: [],
  rootCause: '', lessonsLearned: '', lessonsLearnedReviewDate: null, controlImprovements: [],
  gdprNotifiable: null, nis2Notifiable: null, notificationDetails: '',
  personalDataInvolved: false, affectedRecordsCount: null, regulatoryReportable: [],
  gdprNotificationRequired: false, gdprNotifiedAt: null,
  dataSubjectNotificationRequired: false, dataSubjectsNotifiedAt: null,
  nis2Applicable: false, nis2EarlyWarningAt: null,
  nis2NotificationAt: null, nis2FinalReportAt: null,
  linkedControlIds: [], evidenceIds: [], linkedNonconformityIds: [],
  linkedSupplierIds: [], linkedDocumentIds: [], linkedLegalRegisterIds: [],
  notifiedAuthorityIds: [],
  norms: [],
  status: 'Open',
};

function incidentToForm(inc) {
  return {
    title:                inc.title                || '',
    serviceDeskRef:       inc.serviceDeskRef        || '',
    description:          inc.description           || '',
    detectedAt:           (inc.detectedAt || '').slice(0, 16),
    reportedAt:           (inc.reportedAt || '').slice(0, 16),
    resolvedAt:           (inc.resolvedAt || '').slice(0, 16),
    timezone:             inc.timezone              || 'Europe/Madrid',
    reportedBy:           inc.reportedBy            || '',
    incidentCoordinator:  inc.incidentCoordinator   || '',
    category:             Array.isArray(inc.category) ? inc.category : [],
    categoryOther:        inc.categoryOther         || '',
    severity:             inc.severity              || 'Medium',
    ciaImpact:            Array.isArray(inc.ciaImpact) ? inc.ciaImpact : [],
    linkedAssetIds:       Array.isArray(inc.linkedAssetIds) ? inc.linkedAssetIds : [],
    affectedServices:     Array.isArray(inc.affectedServices) ? inc.affectedServices : [],
    timeline:             Array.isArray(inc.timeline) ? inc.timeline.map((e) => ({ datetime: (e.datetime || '').slice(0, 16), description: e.description || '' })) : [],
    containmentActions:   Array.isArray(inc.containmentActions) ? inc.containmentActions : [],
    eradicationActions:   Array.isArray(inc.eradicationActions) ? inc.eradicationActions : [],
    recoveryActions:      Array.isArray(inc.recoveryActions) ? inc.recoveryActions : [],
    communicationsLog:    Array.isArray(inc.communicationsLog) ? inc.communicationsLog.map((e) => ({ datetime: (e.datetime || '').slice(0, 16), type: e.type || 'Internal', description: e.description || '' })) : [],
    rootCause:            inc.rootCause             || '',
    lessonsLearned:       inc.lessonsLearned        || '',
    lessonsLearnedReviewDate: inc.lessonsLearnedReviewDate || null,
    controlImprovements:  Array.isArray(inc.controlImprovements) ? inc.controlImprovements : [],
    gdprNotifiable:       inc.gdprNotifiable === true ? true : inc.gdprNotifiable === false ? false : null,
    nis2Notifiable:       inc.nis2Notifiable === true ? true : inc.nis2Notifiable === false ? false : null,
    notificationDetails:  inc.notificationDetails   || '',
    personalDataInvolved:    typeof inc.personalDataInvolved === 'boolean' ? inc.personalDataInvolved : false,
    affectedRecordsCount:    typeof inc.affectedRecordsCount === 'number' ? inc.affectedRecordsCount : null,
    regulatoryReportable:    Array.isArray(inc.regulatoryReportable) ? inc.regulatoryReportable : [],
    gdprNotificationRequired:        typeof inc.gdprNotificationRequired === 'boolean' ? inc.gdprNotificationRequired : inc.gdprNotifiable === true,
    gdprNotifiedAt:                  (inc.gdprNotifiedAt || '').slice(0, 16) || null,
    dataSubjectNotificationRequired: typeof inc.dataSubjectNotificationRequired === 'boolean' ? inc.dataSubjectNotificationRequired : false,
    dataSubjectsNotifiedAt:          (inc.dataSubjectsNotifiedAt || '').slice(0, 16) || null,
    nis2Applicable:                  typeof inc.nis2Applicable === 'boolean' ? inc.nis2Applicable : inc.nis2Notifiable === true,
    nis2EarlyWarningAt:              (inc.nis2EarlyWarningAt || '').slice(0, 16) || null,
    nis2NotificationAt:              (inc.nis2NotificationAt || '').slice(0, 16) || null,
    nis2FinalReportAt:               (inc.nis2FinalReportAt || '').slice(0, 16) || null,
    linkedControlIds:     Array.isArray(inc.linkedControlIds) ? inc.linkedControlIds : (Array.isArray(inc.controlIds) ? inc.controlIds : []),
    evidenceIds:          Array.isArray(inc.evidenceIds) ? inc.evidenceIds : [],
    linkedNonconformityIds: Array.isArray(inc.linkedNonconformityIds) ? inc.linkedNonconformityIds : [],
    linkedSupplierIds:       Array.isArray(inc.linkedSupplierIds) ? inc.linkedSupplierIds : [],
    linkedDocumentIds:       Array.isArray(inc.linkedDocumentIds) ? inc.linkedDocumentIds : [],
    linkedLegalRegisterIds:  Array.isArray(inc.linkedLegalRegisterIds) ? inc.linkedLegalRegisterIds : [],
    notifiedAuthorityIds:    Array.isArray(inc.notifiedAuthorityIds) ? inc.notifiedAuthorityIds : [],
    norms:                Array.isArray(inc.norms) ? inc.norms : [],
    status:               inc.status                || 'Open',
  };
}

// ── SHARED STYLES ─────────────────────────────────────────────────────────────
const labelSt = {
  ...FONT_SMALL,
  display: 'block', color: TEXT_MUTED,
  letterSpacing: '0.05em', textTransform: 'uppercase', marginBottom: '0.3rem',
};
const inputSt = (hasErr) => ({
  ...FONT_BODY,
  width: '100%', padding: '0.55rem 0.7rem',
  border: `1px solid ${hasErr ? FIELD_ERROR_BORDER : BORDER_DEFAULT}`,
  borderRadius: 6, background: SURFACE_WHITE,
  color: TEXT_HEADING, outline: 'none', boxSizing: 'border-box',
});
const textareaSt = (hasErr) => ({ ...inputSt(hasErr), resize: 'vertical', minHeight: 72 });
const selectSt = { ...inputSt(false), cursor: 'pointer' };
const sectionHeading = {
  ...FONT_BODY_STRONG,
  color: TEXT_HEADING,
  margin: '0 0 1rem', paddingBottom: '0.5rem',
  borderBottom: `1px solid ${BORDER_DEFAULT}`,
};
const fieldWrap = { marginBottom: '1rem' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 1.5rem' };

function ErrMsg({ msg }) {
  if (!msg) return null;
  return <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0.2rem 0 0' }}>{msg}</p>;
}

// ── COMPONENT ─────────────────────────────────────────────────────────────────
export default function IncidentForm({ incident, mode, org, lang, onSave, onCancel, onFormChange, saveRequestTick, dirty }) {
  const t      = T[lang] || T.en;
  const isEdit = !!incident;
  const isView = mode === 'view';
  const [currentMode, setCurrentMode] = useState(mode || (isEdit ? 'edit' : 'create'));
  const [savedRecord, setSavedRecord] = useState(isEdit ? incident : null);
  const hasChangedRef = useRef(false);

  const [form, setForm] = useState(() => isEdit ? incidentToForm(incident) : { ...EMPTY_FORM });
  const [fileId,            setFileId]            = useState(isEdit ? incident.id  : null);
  const [ref,               setRef]               = useState(isEdit ? incident.ref : null);
  const [errors,            setErrors]            = useState({});
  const [saveState,         setSaveState]         = useState('idle');
  const [saveErrorOpen,     setSaveErrorOpen]     = useState(false);
  const [confirmState,      setConfirmState]      = useState(null);
  const [deleteError,       setDeleteError]       = useState(null);
  const [assetOptions,      setAssetOptions]      = useState([]);
  const [controlOptions,    setControlOptions]    = useState([]);
  const [supplierOptions,      setSupplierOptions]      = useState([]);
  const [ncOptions,            setNcOptions]            = useState([]);
  const [legalRegisterOptions, setLegalRegisterOptions] = useState([]);
  const [authorityOptions,     setAuthorityOptions]     = useState([]);
  const [aiSuggestingResponse,    setAiSuggestingResponse]    = useState(false);
  const [aiErrorResponse,         setAiErrorResponse]         = useState(null);
  const [aiSuggestingPostIncident, setAiSuggestingPostIncident] = useState(false);
  const [aiErrorPostIncident,      setAiErrorPostIncident]      = useState(null);

  useEffect(() => {
    if (isEdit) return;
    const incidentPrefix = (org.refPrefixes && org.refPrefixes.incident) || 'inc';
    generateIncidentIds(org.id, incidentPrefix)
      .then(({ fileId: fid, ref: r }) => { setFileId(fid); setRef(r); })
      .catch(() => { setFileId(`inc-${yyyymmdd()}-001`); setRef(`${incidentPrefix}-${new Date().getFullYear()}-001`); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    listOrgCollection(org.id, 'assets')
      .then((a) => setAssetOptions(a.map((x) => ({ id: x.id, label: `${x.ref || x.id} — ${x.name}` }))))
      .catch(() => setAssetOptions([]));
    readOrgFile(org.id, 'controls/soa.json')
      .then((soa) => setControlOptions((soa.controls || []).map((c) => ({ id: c.id, label: `${aId(c.id)} — ${lang === 'es' ? c.title_es : c.title}` }))))
      .catch(() => setControlOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    listOrgCollection(org.id, 'suppliers')
      .then((a) => setSupplierOptions(a.map((s) => ({ id: s.id, label: s.name || s.ref || s.id }))))
      .catch(() => setSupplierOptions([]));
    listOrgCollection(org.id, 'nonconformities')
      .then((a) => setNcOptions(a.map((nc) => ({ id: nc.id, label: nc.ref || nc.id }))))
      .catch(() => setNcOptions([]));
    listOrgCollection(org.id, 'legal-register')
      .then((a) => setLegalRegisterOptions(a.map((lr) => ({ id: lr.id, label: lr.title || lr.ref || lr.id }))))
      .catch(() => setLegalRegisterOptions([]));
    listOrgCollection(org.id, 'authorities')
      .then((a) => {
        const sorted = [...a].sort((x, y) => (x.ref || '').localeCompare(y.ref || ''));
        setAuthorityOptions(sorted.map((au) => ({
          id: au.id,
          label: `${au.ref || au.id} — ${au.shortName || au.name || au.id}${au.status === 'Inactive' ? (T[lang] || T.en).authorityInactiveSuffix : ''}`,
        })));
      })
      .catch(() => setAuthorityOptions([]));
  }, [org.id, lang]);

  const markChange = () => {
    if (!isView && !hasChangedRef.current && onFormChange) {
      hasChangedRef.current = true;
      onFormChange();
    }
  };

  const sf  = (field) => (val) => { markChange(); setForm((p) => ({ ...p, [field]: val })); };
  const hic = (field) => (e)   => sf(field)(e.target.value);

  const validate = () => {
    const errs = {};
    if (!form.title.trim())      errs.title      = t.errorRequired;
    if (!form.detectedAt)        errs.detectedAt  = t.errorRequired;
    if (!form.reportedBy.trim()) errs.reportedBy  = t.errorRequired;
    if (!form.severity)          errs.severity    = t.errorRequired;
    if (!form.status)            errs.status      = t.errorRequired;
    return errs;
  };

  const handleSave = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    if (!fileId || !ref) return;
    // §11 rule 34 — immutability: a Closed incident cannot be re-saved
    // through the form. Short-circuit before any FileSystemAdapter write.
    if (savedRecord && isRecordImmutable(savedRecord, 'incident')) return;
    setSaveState('saving');
    const now = new Date().toISOString();
    try {
      const payload = {
        id: fileId, orgId: org.id, ref,
        serviceDeskRef:       form.serviceDeskRef.trim() || null,
        title:                form.title.trim(),
        description:          form.description.trim(),
        detectedAt:           form.detectedAt || null,
        reportedAt:           form.reportedAt || null,
        resolvedAt:           form.resolvedAt || null,
        reportedBy:           form.reportedBy.trim(),
        incidentCoordinator:  form.incidentCoordinator || null,
        category:             form.category,
        categoryOther:        form.category.includes('Other') ? form.categoryOther.trim() || null : null,
        timezone:             form.timezone || 'Europe/Madrid',
        severity:             form.severity,
        ciaImpact:            form.ciaImpact,
        linkedAssetIds:       form.linkedAssetIds,
        affectedServices:     form.affectedServices,
        timeline:             [...form.timeline].sort((a, b) => (a.datetime || '').localeCompare(b.datetime || '')),
        containmentActions:   form.containmentActions,
        eradicationActions:   form.eradicationActions,
        recoveryActions:      form.recoveryActions,
        communicationsLog:    [...form.communicationsLog].sort((a, b) => (a.datetime || '').localeCompare(b.datetime || '')),
        rootCause:            form.rootCause.trim() || null,
        lessonsLearned:       form.lessonsLearned.trim() || null,
        lessonsLearnedReviewDate: form.lessonsLearnedReviewDate || null,
        gdprNotifiable:       form.gdprNotifiable,
        nis2Notifiable:       form.nis2Notifiable,
        personalDataInvolved: form.personalDataInvolved,
        affectedRecordsCount: form.affectedRecordsCount === null || form.affectedRecordsCount === ''
          ? null
          : Number(form.affectedRecordsCount),
        regulatoryReportable: form.regulatoryReportable,
        gdprNotificationRequired:        form.gdprNotificationRequired,
        gdprNotifiedAt:                  form.gdprNotifiedAt || null,
        dataSubjectNotificationRequired: form.dataSubjectNotificationRequired,
        dataSubjectsNotifiedAt:          form.dataSubjectsNotifiedAt || null,
        nis2Applicable:                  form.nis2Applicable,
        nis2EarlyWarningAt:              form.nis2EarlyWarningAt || null,
        nis2NotificationAt:              form.nis2NotificationAt || null,
        nis2FinalReportAt:               form.nis2FinalReportAt || null,
        notificationDetails:  form.notificationDetails.trim() || null,
        controlImprovements:  form.controlImprovements,
        linkedControlIds:     form.linkedControlIds,
        linkedNonconformityIds: form.linkedNonconformityIds,
        linkedSupplierIds:       form.linkedSupplierIds,
        linkedDocumentIds:       form.linkedDocumentIds,
        linkedLegalRegisterIds:  form.linkedLegalRegisterIds,
        notifiedAuthorityIds:    form.notifiedAuthorityIds,
        evidenceIds:          form.evidenceIds,
        norms:                (Array.isArray(form.norms) && form.norms.length > 0)
          ? form.norms
          : (Array.isArray(org.norms) && org.norms.length > 0 ? [org.norms[0]] : []),
        status:               form.status,
        closedAt:             savedRecord?.closedAt || null,
        createdAt:            savedRecord?.createdAt || now,
        updatedAt:            now,
      };
      payload.changeLog = savedRecord ? appendChangeLog(savedRecord, payload, org.consultantPersonId || 'system') : [];
      await writeOrgFile(org.id, `incidents/${fileId}.json`, payload);
      setSavedRecord(payload);
      if (currentMode !== 'edit') setCurrentMode('edit');
      onSave(fileId);
      setSaveState('ok');
      setTimeout(() => setSaveState('idle'), 2000);
    } catch {
      setSaveState('error');
      // §11 rule 37 — surface the blocking dialog so the user picks either
      // Retry (re-invokes this handler) or Discard (reverts to savedRecord).
      // The inline banner stays visible behind the dialog per §6 UI feedback.
      setSaveErrorOpen(true);
      setTimeout(() => setSaveState('idle'), 3000);
    }
  };

  // §11 rule 37 — Discard: revert the form to the last successful snapshot
  // and clear dirty. For a never-saved record (no savedRecord), discard
  // closes the form entirely via onCancel so the user doesn't keep editing
  // a phantom record.
  const handleDiscardChanges = () => {
    setSaveErrorOpen(false);
    if (savedRecord) {
      setForm(incidentToForm(savedRecord));
      hasChangedRef.current = false;
      setErrors({});
      if (onFormChange) {
        // Clearing dirty at the parent is done by resetting hasChangedRef —
        // the next edit will re-fire onFormChange. We explicitly call onCancel
        // here to ensure the parent's dirty flag resets to false cleanly.
      }
    }
    onCancel();
  };

  const handleRetrySave = () => {
    setSaveErrorOpen(false);
    handleSave();
  };

  useEffect(() => {
    if (saveRequestTick > 0) handleSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveRequestTick]);

  const handleDelete = async () => {
    try { await deleteOrgFile(org.id, `incidents/${fileId}.json`); onSave(null); }
    catch { setDeleteError(t.errorDelete); setConfirmState(null); }
  };

  // timeline helpers
  const addTimelineEntry = () => sf('timeline')([...form.timeline, { datetime: '', description: '' }]);
  const updateTimeline   = (idx, field, val) => {
    const arr = [...form.timeline]; arr[idx] = { ...arr[idx], [field]: val }; sf('timeline')(arr);
  };
  const removeTimeline   = (idx) => sf('timeline')(form.timeline.filter((_, i) => i !== idx));

  // comms helpers
  const addCommEntry = () => sf('communicationsLog')([...form.communicationsLog, { datetime: '', type: 'Internal', description: '' }]);
  const updateComm   = (idx, field, val) => {
    const arr = [...form.communicationsLog]; arr[idx] = { ...arr[idx], [field]: val }; sf('communicationsLog')(arr);
  };
  const removeComm   = (idx) => sf('communicationsLog')(form.communicationsLog.filter((_, i) => i !== idx));

  const handleAISuggestResponse = async () => {
    setAiSuggestingResponse(true);
    setAiErrorResponse(null);
    const prompt = `You are an information security incident response expert.\nGiven the following incident, suggest concrete response actions for each phase.\nRespond ONLY with a valid JSON object, no markdown, no preamble.\nTitle: ${form.title}\nDescription: ${form.description}\nCategory: ${form.category.join(', ')}\nSeverity: ${form.severity}\nReturn exactly this JSON:\n{\n"containmentActions": ["..."],\n"eradicationActions": ["..."],\n"recoveryActions": ["..."]\n}\nEach array 2-4 specific actionable items.`;
    const result = await callWorker({ prompt, maxTokens: 600 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiErrorResponse(busy ? t.aiErrorOverloaded : t.aiError);
      setAiSuggestingResponse(false);
      return;
    }
    let parsed;
    try { parsed = JSON.parse(result.text); } catch {
      setAiErrorResponse(t.aiError);
      setAiSuggestingResponse(false);
      return;
    }
    if (Array.isArray(parsed.containmentActions) && form.containmentActions.length === 0) sf('containmentActions')(parsed.containmentActions);
    if (Array.isArray(parsed.eradicationActions) && form.eradicationActions.length === 0) sf('eradicationActions')(parsed.eradicationActions);
    if (Array.isArray(parsed.recoveryActions) && form.recoveryActions.length === 0) sf('recoveryActions')(parsed.recoveryActions);
    setAiSuggestingResponse(false);
  };

  const handleAISuggestPostIncident = async () => {
    setAiSuggestingPostIncident(true);
    setAiErrorPostIncident(null);
    const prompt = `You are an information security incident response expert.\nGiven the following incident, suggest root cause and control improvements.\nRespond ONLY with a valid JSON object, no markdown, no preamble.\nTitle: ${form.title}\nDescription: ${form.description}\nCategory: ${form.category.join(', ')}\nContainment actions: ${form.containmentActions.join(', ')}\nReturn exactly this JSON:\n{\n"rootCause": "concise root cause analysis in 2-3 sentences",\n"controlImprovements": ["..."]\n}\ncontrolImprovements 2-4 items referencing ISO 27001 Annex A controls where applicable.`;
    const result = await callWorker({ prompt, maxTokens: 600 });
    if (!result.ok) {
      const busy = result.error === 'overloaded' || result.error === 'timeout';
      setAiErrorPostIncident(busy ? t.aiErrorOverloaded : t.aiError);
      setAiSuggestingPostIncident(false);
      return;
    }
    let parsed;
    try { parsed = JSON.parse(result.text); } catch {
      setAiErrorPostIncident(t.aiError);
      setAiSuggestingPostIncident(false);
      return;
    }
    if (parsed.rootCause && !form.rootCause) sf('rootCause')(parsed.rootCause);
    if (Array.isArray(parsed.controlImprovements)) sf('controlImprovements')([...form.controlImprovements, ...parsed.controlImprovements]);
    setAiSuggestingPostIncident(false);
  };

  const disabled = saveState === 'saving' || isView;
  // §11 rule 34 — read-only banner shown only when the form is in view mode
  // because the incident is in a terminal/immutable status (Closed), not when
  // the user voluntarily opened a non-immutable incident in view mode.
  const frozen = isView && savedRecord && isRecordImmutable(savedRecord, 'incident');
  const btnPrimary   = { ...BTN_PRIMARY, ...FONT_BUTTON, background: saveState === 'saving' ? TEXT_MUTED : saveState === 'ok' ? BRAND_PRIMARY_DARK : BTN_PRIMARY.background, border: 'none', borderRadius: 8, padding: '0.55rem 1.25rem', cursor: disabled ? 'default' : 'pointer' };
  const btnSecondary = { ...BTN_SECONDARY, ...FONT_BUTTON, borderRadius: 8, padding: '0.55rem 1.25rem', cursor: disabled ? 'default' : 'pointer' };
  const rowRemoveBtn = { ...BTN_DESTRUCTIVE_OUTLINE, ...FONT_SMALL, borderRadius: 5, padding: '0.2rem 0.45rem', cursor: 'pointer', flexShrink: 0 };
  const addRowBtn    = { ...FONT_SMALL, background: 'none', border: `1px dashed ${BORDER_DEFAULT}`, borderRadius: 6, color: TEXT_MUTED, padding: '0.35rem 0.75rem', cursor: 'pointer', width: '100%', textAlign: 'center' };
  const optSpan      = { ...FONT_TINY, color: TEXT_DISABLED, textTransform: 'none', letterSpacing: 0 };

  return (
    <div style={{ background: BG_SURFACE, borderRadius: 12, border: `1px solid ${BORDER_DEFAULT}`, padding: '2rem' }}>
      <ConfirmDialog
        open={confirmState !== null}
        message={t.btnConfirmDelete}
        onConfirm={handleDelete}
        onCancel={() => setConfirmState(null)}
        confirmLabel={confirmState?.confirmLabel}
      />

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h2 style={{ ...FONT_SECTION_TITLE, color: TEXT_HEADING, margin: 0 }}>
          {isView ? t.titleView : (currentMode === 'edit' ? t.titleEdit : t.titleCreate)}
          {ref && <span style={{ ...FONT_LABEL, color: TEXT_MUTED, marginLeft: '0.75rem' }}>{ref}</span>}
          {!ref && !isView && currentMode !== 'edit' && <span style={{ ...FONT_SMALL, color: TEXT_DISABLED, marginLeft: '0.5rem' }}>{t.generating}</span>}
        </h2>
        {!isView && <p style={{ ...FONT_SMALL, color: TEXT_DISABLED, margin: '0.35rem 0 0' }}>{t.requiredLegend}</p>}
      </div>

      {frozen && (
        <div style={{
          background: STATUS_NEUTRAL.bg, border: `1px solid ${BORDER_DEFAULT}`,
          borderRadius: 8, padding: '0.75rem 1rem', marginBottom: '1rem',
          ...FONT_LABEL, color: TEXT_DEFAULT,
        }}>
          {t.frozenBanner}
        </div>
      )}

      <fieldset disabled={disabled} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      {/* ── S1: Identification ──────────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s1}</h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fTitle} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
            <input value={form.title} onChange={hic('title')} disabled={disabled} style={inputSt(!!errors.title)} />
            <ErrMsg msg={errors.title} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fServiceDeskRef} <span style={optSpan}>{t.optional}</span></label>
            <input value={form.serviceDeskRef} onChange={hic('serviceDeskRef')} disabled={disabled} style={inputSt(false)} />
          </div>
          <div style={{ ...fieldWrap, gridColumn: 'span 2' }}>
            <label style={labelSt}>{t.fDescription}</label>
            <textarea value={form.description} onChange={hic('description')} disabled={disabled} style={textareaSt(false)} />
          </div>
        </div>
      </div>

      {/* ── S2: People ──────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s2}</h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fDetectedAt} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
            <input type="datetime-local" value={form.detectedAt} onChange={hic('detectedAt')} disabled={disabled} style={inputSt(!!errors.detectedAt)} />
            <ErrMsg msg={errors.detectedAt} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fReportedBy} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
            <PeopleSelector orgId={org.id} lang={lang} value={form.reportedBy} onChange={sf('reportedBy')} allowFreeText disabled={disabled} />
            <ErrMsg msg={errors.reportedBy} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fTimezone}</label>
            <select value={form.timezone} onChange={hic('timezone')} disabled={disabled} style={selectSt}>
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fIncidentCoordinator} <span style={optSpan}>{t.optional}</span></label>
            <PeopleSelector orgId={org.id} lang={lang} value={form.incidentCoordinator} onChange={sf('incidentCoordinator')} disabled={disabled} />
          </div>
        </div>
      </div>

      {/* ── S3: Classification ──────────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s3}</h3>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fCategory}</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
            {CATEGORIES.map((cat) => {
              const sel = form.category.includes(cat);
              return (
                <button key={cat} type="button" disabled={disabled}
                  onClick={() => {
                    if (sel) {
                      sf('category')(form.category.filter((c) => c !== cat));
                      if (cat === 'Other') sf('categoryOther')('');
                    } else {
                      sf('category')([...form.category, cat]);
                    }
                  }}
                  style={{
                    ...FONT_SMALL,
                    fontWeight: 500,
                    padding: '0.25rem 0.6rem', borderRadius: 5,
                    border: `1px solid ${sel ? BRAND_PRIMARY : BORDER_DEFAULT}`,
                    background: sel ? STATUS_GOOD.bg : SURFACE_WHITE,
                    color: sel ? BRAND_PRIMARY_DARK : TEXT_MUTED,
                    cursor: disabled ? 'default' : 'pointer',
                  }}
                >{t.categoryLabels[cat]}</button>
              );
            })}
          </div>
          {form.category.includes('Other') && (
            <div style={{ marginTop: '0.5rem' }}>
              <label style={{ ...labelSt, fontSize: '0.65rem' }}>{t.fCategoryOther}</label>
              <input value={form.categoryOther} onChange={hic('categoryOther')} disabled={disabled} style={inputSt(false)} />
            </div>
          )}
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fSeverity} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
          <select value={form.severity} onChange={hic('severity')} disabled={disabled} style={selectSt}>
            {SEVERITIES.map((v) => <option key={v} value={v}>{t.severityLabels[v]}</option>)}
          </select>
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fCiaImpact}</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
            {[
              { val: 'C', label: t.fConfidentialityImpact },
              { val: 'I', label: t.fIntegrityImpact },
              { val: 'A', label: t.fAvailabilityImpact },
            ].map((o) => {
              const sel = form.ciaImpact.includes(o.val);
              return (
                <button key={o.val} type="button" disabled={disabled}
                  onClick={() => {
                    if (sel) sf('ciaImpact')(form.ciaImpact.filter((x) => x !== o.val));
                    else     sf('ciaImpact')([...form.ciaImpact, o.val]);
                  }}
                  style={{
                    ...FONT_SMALL,
                    fontWeight: 500,
                    padding: '0.25rem 0.6rem', borderRadius: 5,
                    border: `1px solid ${sel ? BRAND_PRIMARY : BORDER_DEFAULT}`,
                    background: sel ? STATUS_GOOD.bg : SURFACE_WHITE,
                    color: sel ? BRAND_PRIMARY_DARK : TEXT_MUTED,
                    cursor: disabled ? 'default' : 'pointer',
                  }}
                >{o.label}</button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── S4: Affected scope ──────────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s4}</h3>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fAffectedAssets}</label>
          {assetOptions.length > 0 ? (
            <EntitySelector items={assetOptions} selectedIds={form.linkedAssetIds}
              onAdd={(id) => sf('linkedAssetIds')([...form.linkedAssetIds, id])}
              onRemove={(id) => sf('linkedAssetIds')(form.linkedAssetIds.filter((x) => x !== id))}
              placeholder={t.searchAsset} disabled={disabled} chipBg={STATUS_WARN.bg} chipColor={STATUS_WARN.text} />
          ) : (
            <TagInput tags={form.linkedAssetIds}
              onAdd={(v) => sf('linkedAssetIds')([...form.linkedAssetIds, v])}
              onRemove={(v) => sf('linkedAssetIds')(form.linkedAssetIds.filter((x) => x !== v))}
              onUpdate={(oldValue, newValue) => sf('linkedAssetIds')(form.linkedAssetIds.map((x) => x === oldValue ? newValue : x))}
              placeholder={t.searchAsset} disabled={disabled} />
          )}
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fAffectedServices}</label>
          <TagInput tags={form.affectedServices}
            onAdd={(v) => sf('affectedServices')([...form.affectedServices, v])}
            onRemove={(v) => sf('affectedServices')(form.affectedServices.filter((x) => x !== v))}
            onUpdate={(oldValue, newValue) => sf('affectedServices')(form.affectedServices.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.servicePlaceholder} disabled={disabled} />
        </div>
      </div>

      {/* ── S5: Response ────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '1rem', ...sectionHeading }}>
          <span>{t.s5}</span>
          <AISuggestButton onClick={handleAISuggestResponse} loading={aiSuggestingResponse} disabled={disabled || !form.title.trim() || !org.aiProcessingConsent} lang={lang} />
          {!org.aiProcessingConsent && <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>{t.aiConsentRequired}</span>}
        </div>
        {aiErrorResponse && <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '-0.5rem 0 1rem' }}>{aiErrorResponse}</p>}
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fContainmentActions}<span className="ai-spark">✦</span></label>
          <TagInput tags={form.containmentActions}
            onAdd={(v) => sf('containmentActions')([...form.containmentActions, v])}
            onRemove={(v) => sf('containmentActions')(form.containmentActions.filter((x) => x !== v))}
            onUpdate={(oldValue, newValue) => sf('containmentActions')(form.containmentActions.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.actionPlaceholder} disabled={disabled} />
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fEradicationActions}<span className="ai-spark">✦</span></label>
          <TagInput tags={form.eradicationActions}
            onAdd={(v) => sf('eradicationActions')([...form.eradicationActions, v])}
            onRemove={(v) => sf('eradicationActions')(form.eradicationActions.filter((x) => x !== v))}
            onUpdate={(oldValue, newValue) => sf('eradicationActions')(form.eradicationActions.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.actionPlaceholder} disabled={disabled} />
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fRecoveryActions}<span className="ai-spark">✦</span></label>
          <TagInput tags={form.recoveryActions}
            onAdd={(v) => sf('recoveryActions')([...form.recoveryActions, v])}
            onRemove={(v) => sf('recoveryActions')(form.recoveryActions.filter((x) => x !== v))}
            onUpdate={(oldValue, newValue) => sf('recoveryActions')(form.recoveryActions.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.actionPlaceholder} disabled={disabled} />
        </div>
      </div>

      {/* ── S6: Timeline ────────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s6}</h3>
        {form.timezone && <p style={{ ...FONT_TINY, color: TEXT_DISABLED, margin: '-0.5rem 0 0.75rem' }}>{form.timezone}</p>}
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fReportedAt} <span style={optSpan}>{t.optional}</span></label>
            <input type="datetime-local" value={form.reportedAt} onChange={hic('reportedAt')} disabled={disabled} style={inputSt(false)} />
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fResolvedAt} <span style={optSpan}>{t.optional}</span></label>
            <input type="datetime-local" value={form.resolvedAt} onChange={hic('resolvedAt')} disabled={disabled} style={inputSt(false)} />
          </div>
        </div>
        {form.timeline.map((entry, idx) => (
          <div key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
            <div style={{ flex: '0 0 220px' }}>
              {idx === 0 && <label style={{ ...labelSt, fontSize: '0.65rem' }}>{t.fTimelineDatetime}</label>}
              <input type="datetime-local" value={entry.datetime} onChange={(e) => updateTimeline(idx, 'datetime', e.target.value)} disabled={disabled} style={inputSt(false)} />
            </div>
            <div style={{ flex: 1 }}>
              {idx === 0 && <label style={{ ...labelSt, fontSize: '0.65rem' }}>{t.fTimelineDescription}</label>}
              <input value={entry.description} onChange={(e) => updateTimeline(idx, 'description', e.target.value)} disabled={disabled} style={inputSt(false)} />
            </div>
            <div style={{ paddingTop: idx === 0 ? '1.2rem' : 0 }}>
              <button type="button" onClick={() => removeTimeline(idx)} disabled={disabled} style={rowRemoveBtn}>×</button>
            </div>
          </div>
        ))}
        <button type="button" onClick={addTimelineEntry} disabled={disabled} style={addRowBtn}>{t.fAddTimelineEntry}</button>
      </div>

      {/* ── S7: Communications ──────────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s7}</h3>
        {form.timezone && <p style={{ ...FONT_TINY, color: TEXT_DISABLED, margin: '-0.5rem 0 0.75rem' }}>{form.timezone}</p>}
        {form.communicationsLog.map((entry, idx) => (
          <div key={idx} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
            <div style={{ flex: '0 0 220px' }}>
              {idx === 0 && <label style={{ ...labelSt, fontSize: '0.65rem' }}>{t.fCommDatetime}</label>}
              <input type="datetime-local" value={entry.datetime} onChange={(e) => updateComm(idx, 'datetime', e.target.value)} disabled={disabled} style={inputSt(false)} />
            </div>
            <div style={{ flex: '0 0 160px' }}>
              {idx === 0 && <label style={{ ...labelSt, fontSize: '0.65rem' }}>{t.fCommType}</label>}
              <select value={entry.type} onChange={(e) => updateComm(idx, 'type', e.target.value)} disabled={disabled} style={selectSt}>
                {COMM_TYPES.map((ct) => <option key={ct} value={ct}>{t.commTypeLabels[ct]}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              {idx === 0 && <label style={{ ...labelSt, fontSize: '0.65rem' }}>{t.fCommDescription}</label>}
              <input value={entry.description} onChange={(e) => updateComm(idx, 'description', e.target.value)} disabled={disabled} style={inputSt(false)} />
            </div>
            <div style={{ paddingTop: idx === 0 ? '1.2rem' : 0 }}>
              <button type="button" onClick={() => removeComm(idx)} disabled={disabled} style={rowRemoveBtn}>×</button>
            </div>
          </div>
        ))}
        <button type="button" onClick={addCommEntry} disabled={disabled} style={addRowBtn}>{t.fAddCommEntry}</button>
      </div>

      {/* ── S8: Post-incident ───────────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: '1rem', ...sectionHeading }}>
          <span>{t.s8}</span>
          <AISuggestButton onClick={handleAISuggestPostIncident} loading={aiSuggestingPostIncident} disabled={disabled || !form.title.trim() || !org.aiProcessingConsent} lang={lang} />
          {!org.aiProcessingConsent && <span style={{ ...FONT_SMALL, color: TEXT_MUTED }}>{t.aiConsentRequired}</span>}
        </div>
        {aiErrorPostIncident && <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '-0.5rem 0 1rem' }}>{aiErrorPostIncident}</p>}
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fRootCause}<span className="ai-spark">✦</span></label>
          <textarea value={form.rootCause} onChange={hic('rootCause')} disabled={disabled} style={textareaSt(false)} />
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fLessonsLearned}</label>
          <textarea value={form.lessonsLearned} onChange={hic('lessonsLearned')} disabled={disabled} style={textareaSt(false)} />
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fLessonsLearnedReviewDate} <span style={optSpan}>{t.optional}</span></label>
          <input type="date" value={form.lessonsLearnedReviewDate || ''} onChange={(e) => sf('lessonsLearnedReviewDate')(e.target.value || null)} disabled={disabled} style={inputSt(false)} />
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fControlImprovements}<span className="ai-spark">✦</span></label>
          <TagInput tags={form.controlImprovements}
            onAdd={(v) => sf('controlImprovements')([...form.controlImprovements, v])}
            onRemove={(v) => sf('controlImprovements')(form.controlImprovements.filter((x) => x !== v))}
            onUpdate={(oldValue, newValue) => sf('controlImprovements')(form.controlImprovements.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.improvePlaceholder} disabled={disabled} />
        </div>
      </div>

      {/* ── S9: Notifications ───────────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s9}</h3>

        {/* Personal data involved — gates GDPR-related fields */}
        <div style={fieldWrap}>
          <label style={{ ...FONT_BODY, display: 'flex', alignItems: 'center', gap: '0.5rem', color: TEXT_HEADING, cursor: disabled ? 'default' : 'pointer' }}>
            <input type="checkbox" checked={form.personalDataInvolved} onChange={(e) => sf('personalDataInvolved')(e.target.checked)} disabled={disabled} />
            {t.fPersonalDataInvolved}
          </label>
        </div>
        {form.personalDataInvolved && (
          <div style={grid2}>
            <div style={fieldWrap}>
              <label style={labelSt}>{t.fAffectedRecordsCount} <span style={optSpan}>{t.optional}</span></label>
              <input
                type="number" min="0"
                value={form.affectedRecordsCount === null || form.affectedRecordsCount === undefined ? '' : form.affectedRecordsCount}
                onChange={(e) => sf('affectedRecordsCount')(e.target.value === '' ? null : Number(e.target.value))}
                placeholder={t.recordsPlaceholder}
                disabled={disabled}
                style={inputSt(false)}
              />
            </div>
            <div style={fieldWrap}>
              <label style={labelSt}>{t.fRegulatoryReportable} <span style={optSpan}>{t.optional}</span></label>
              <TagInput tags={form.regulatoryReportable}
                onAdd={(v) => sf('regulatoryReportable')([...form.regulatoryReportable, v])}
                onRemove={(v) => sf('regulatoryReportable')(form.regulatoryReportable.filter((x) => x !== v))}
                onUpdate={(oldValue, newValue) => sf('regulatoryReportable')(form.regulatoryReportable.map((x) => x === oldValue ? newValue : x))}
                placeholder={t.regulatorPlaceholder} disabled={disabled} />
            </div>
          </div>
        )}

        {/* GDPR group */}
        <div style={{ marginBottom: '1.25rem' }}>
          <p style={{ ...FONT_SMALL, fontWeight: 600, color: TEXT_HEADING, letterSpacing: '0.05em', textTransform: 'uppercase', margin: '0 0 0.6rem' }}>{t.s9Gdpr}</p>
          <div style={fieldWrap}>
            <label style={{ ...FONT_BODY, display: 'flex', alignItems: 'center', gap: '0.5rem', color: TEXT_HEADING, cursor: disabled ? 'default' : 'pointer' }}>
              <input type="checkbox" checked={form.gdprNotificationRequired} onChange={(e) => sf('gdprNotificationRequired')(e.target.checked)} disabled={disabled} />
              {t.fGdprNotificationRequired}
            </label>
          </div>
          {form.gdprNotificationRequired && (
            <div style={grid2}>
              <div style={fieldWrap}>
                <label style={labelSt}>{t.fGdprNotifiedAt} <span style={optSpan}>{t.optional}</span></label>
                <input type="datetime-local" value={form.gdprNotifiedAt || ''} onChange={(e) => sf('gdprNotifiedAt')(e.target.value || null)} disabled={disabled} style={inputSt(false)} />
              </div>
            </div>
          )}
          <div style={fieldWrap}>
            <label style={{ ...FONT_BODY, display: 'flex', alignItems: 'center', gap: '0.5rem', color: TEXT_HEADING, cursor: disabled ? 'default' : 'pointer' }}>
              <input type="checkbox" checked={form.dataSubjectNotificationRequired} onChange={(e) => sf('dataSubjectNotificationRequired')(e.target.checked)} disabled={disabled} />
              {t.fDataSubjectNotificationRequired}
            </label>
          </div>
          {form.dataSubjectNotificationRequired && (
            <div style={grid2}>
              <div style={fieldWrap}>
                <label style={labelSt}>{t.fDataSubjectsNotifiedAt} <span style={optSpan}>{t.optional}</span></label>
                <input type="datetime-local" value={form.dataSubjectsNotifiedAt || ''} onChange={(e) => sf('dataSubjectsNotifiedAt')(e.target.value || null)} disabled={disabled} style={inputSt(false)} />
              </div>
            </div>
          )}
        </div>

        {/* NIS2 group */}
        <div style={{ marginBottom: '1.25rem' }}>
          <p style={{ ...FONT_SMALL, fontWeight: 600, color: TEXT_HEADING, letterSpacing: '0.05em', textTransform: 'uppercase', margin: '0 0 0.6rem' }}>{t.s9Nis2}</p>
          <div style={fieldWrap}>
            <label style={{ ...FONT_BODY, display: 'flex', alignItems: 'center', gap: '0.5rem', color: TEXT_HEADING, cursor: disabled ? 'default' : 'pointer' }}>
              <input type="checkbox" checked={form.nis2Applicable} onChange={(e) => sf('nis2Applicable')(e.target.checked)} disabled={disabled} />
              {t.fNis2Applicable}
            </label>
          </div>
          {form.nis2Applicable && (
            <div style={grid2}>
              <div style={fieldWrap}>
                <label style={labelSt}>{t.fNis2EarlyWarningAt} <span style={optSpan}>{t.optional}</span></label>
                <input type="datetime-local" value={form.nis2EarlyWarningAt || ''} onChange={(e) => sf('nis2EarlyWarningAt')(e.target.value || null)} disabled={disabled} style={inputSt(false)} />
              </div>
              <div style={fieldWrap}>
                <label style={labelSt}>{t.fNis2NotificationAt} <span style={optSpan}>{t.optional}</span></label>
                <input type="datetime-local" value={form.nis2NotificationAt || ''} onChange={(e) => sf('nis2NotificationAt')(e.target.value || null)} disabled={disabled} style={inputSt(false)} />
              </div>
              <div style={fieldWrap}>
                <label style={labelSt}>{t.fNis2FinalReportAt} <span style={optSpan}>{t.optional}</span></label>
                <input type="datetime-local" value={form.nis2FinalReportAt || ''} onChange={(e) => sf('nis2FinalReportAt')(e.target.value || null)} disabled={disabled} style={inputSt(false)} />
              </div>
            </div>
          )}
        </div>

        <div style={fieldWrap}>
          <label style={labelSt} title={t.ttNotifiedAuthorityIds}>{t.fNotifiedAuthorityIds} <span style={optSpan}>{t.optional}</span></label>
          <EntitySelector items={authorityOptions} selectedIds={form.notifiedAuthorityIds}
            onAdd={(id) => sf('notifiedAuthorityIds')([...form.notifiedAuthorityIds, id])}
            onRemove={(id) => sf('notifiedAuthorityIds')(form.notifiedAuthorityIds.filter((x) => x !== id))}
            placeholder={t.searchAuthority} disabled={disabled} chipBg={STATUS_INFO.bg} chipColor="#1d4ed8" />
          {authorityOptions.length === 0 && (
            <p style={{ ...FONT_TINY, color: TEXT_DISABLED, margin: '0.25rem 0 0' }}>
              {t.noAuthoritiesHint}
            </p>
          )}
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fNotificationDetails} <span style={optSpan}>{t.optional}</span></label>
          <textarea value={form.notificationDetails} onChange={hic('notificationDetails')} disabled={disabled} style={{ ...textareaSt(false), minHeight: 56 }} />
        </div>
      </div>

      {/* ── S10: Relationships ──────────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s10}</h3>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fControlIds}</label>
          {controlOptions.length > 0 ? (
            <EntitySelector items={controlOptions} selectedIds={form.linkedControlIds}
              onAdd={(id) => sf('linkedControlIds')([...form.linkedControlIds, id])}
              onRemove={(id) => sf('linkedControlIds')(form.linkedControlIds.filter((x) => x !== id))}
              placeholder={t.searchControl} disabled={disabled} chipBg={STATUS_INFO.bg} chipColor="#1d4ed8" />
          ) : (
            <TagInput tags={form.linkedControlIds}
              onAdd={(v) => sf('linkedControlIds')([...form.linkedControlIds, v])}
              onRemove={(v) => sf('linkedControlIds')(form.linkedControlIds.filter((x) => x !== v))}
              onUpdate={(oldValue, newValue) => sf('linkedControlIds')(form.linkedControlIds.map((x) => x === oldValue ? newValue : x))}
              placeholder={t.searchControl} disabled={disabled} />
          )}
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fEvidenceIds}</label>
          <TagInput tags={form.evidenceIds}
            onAdd={(v) => sf('evidenceIds')([...form.evidenceIds, v])}
            onRemove={(v) => sf('evidenceIds')(form.evidenceIds.filter((x) => x !== v))}
            onUpdate={(oldValue, newValue) => sf('evidenceIds')(form.evidenceIds.map((x) => x === oldValue ? newValue : x))}
            placeholder={t.evidencePlaceholder} disabled={disabled} />
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fLinkedNonconformityIds} <span style={optSpan}>{t.optional}</span></label>
          {ncOptions.length > 0 ? (
            <EntitySelector items={ncOptions} selectedIds={form.linkedNonconformityIds}
              onAdd={(id) => sf('linkedNonconformityIds')([...form.linkedNonconformityIds, id])}
              onRemove={(id) => sf('linkedNonconformityIds')(form.linkedNonconformityIds.filter((x) => x !== id))}
              placeholder={t.searchNonconformity} disabled={disabled} chipBg={STATUS_INFO.bg} chipColor="#1d4ed8" />
          ) : (
            <TagInput tags={form.linkedNonconformityIds}
              onAdd={(v) => sf('linkedNonconformityIds')([...form.linkedNonconformityIds, v])}
              onRemove={(v) => sf('linkedNonconformityIds')(form.linkedNonconformityIds.filter((x) => x !== v))}
              onUpdate={(oldValue, newValue) => sf('linkedNonconformityIds')(form.linkedNonconformityIds.map((x) => x === oldValue ? newValue : x))}
              placeholder={t.nonconformityPlaceholder} disabled={disabled} />
          )}
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fLinkedSupplierIds} <span style={optSpan}>{t.optional}</span></label>
          {supplierOptions.length > 0 ? (
            <EntitySelector items={supplierOptions} selectedIds={form.linkedSupplierIds}
              onAdd={(id) => sf('linkedSupplierIds')([...form.linkedSupplierIds, id])}
              onRemove={(id) => sf('linkedSupplierIds')(form.linkedSupplierIds.filter((x) => x !== id))}
              placeholder={t.searchSupplier} disabled={disabled} chipBg={STATUS_INFO.bg} chipColor="#1d4ed8" />
          ) : (
            <TagInput tags={form.linkedSupplierIds}
              onAdd={(v) => sf('linkedSupplierIds')([...form.linkedSupplierIds, v])}
              onRemove={(v) => sf('linkedSupplierIds')(form.linkedSupplierIds.filter((x) => x !== v))}
              onUpdate={(oldValue, newValue) => sf('linkedSupplierIds')(form.linkedSupplierIds.map((x) => x === oldValue ? newValue : x))}
              placeholder={t.searchSupplier} disabled={disabled} />
          )}
        </div>
        <div style={fieldWrap}>
          <label style={labelSt}>{t.fLinkedLegalRegisterIds} <span style={optSpan}>{t.optional}</span></label>
          {legalRegisterOptions.length > 0 ? (
            <EntitySelector items={legalRegisterOptions} selectedIds={form.linkedLegalRegisterIds}
              onAdd={(id) => sf('linkedLegalRegisterIds')([...form.linkedLegalRegisterIds, id])}
              onRemove={(id) => sf('linkedLegalRegisterIds')(form.linkedLegalRegisterIds.filter((x) => x !== id))}
              placeholder={t.searchLegalRegister} disabled={disabled} chipBg={STATUS_INFO.bg} chipColor="#1d4ed8" />
          ) : (
            <TagInput tags={form.linkedLegalRegisterIds}
              onAdd={(v) => sf('linkedLegalRegisterIds')([...form.linkedLegalRegisterIds, v])}
              onRemove={(v) => sf('linkedLegalRegisterIds')(form.linkedLegalRegisterIds.filter((x) => x !== v))}
              onUpdate={(oldValue, newValue) => sf('linkedLegalRegisterIds')(form.linkedLegalRegisterIds.map((x) => x === oldValue ? newValue : x))}
              placeholder={t.searchLegalRegister} disabled={disabled} />
          )}
        </div>
      </div>

      {/* ── S11: Lifecycle ──────────────────────────────────────────────────── */}
      <div style={{ marginBottom: '2rem' }}>
        <h3 style={sectionHeading}>{t.s11}</h3>
        <div style={grid2}>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fStatus} <span style={{ color: ERROR_TEXT }}>{t.required}</span></label>
            <select value={form.status} onChange={hic('status')} disabled={disabled} style={selectSt}>
              {STATUSES.map((v) => <option key={v} value={v}>{t.statusLabels[v]}</option>)}
            </select>
          </div>
          <div style={fieldWrap}>
            <label style={labelSt}>{t.fClosedAt}</label>
            <span style={{ ...FONT_LABEL, color: TEXT_MUTED }}>
              {savedRecord?.closedAt || t.notSet}
            </span>
          </div>
        </div>
      </div>

      </fieldset>

      {/* ── Sticky action bar ─────────────────────────────────────────────── */}
      {!isView && (
        <div style={{
          position: 'sticky',
          bottom: 0,
          marginLeft: '-2rem',
          marginRight: '-2rem',
          marginBottom: '-2rem',
          marginTop: '1.5rem',
          padding: '0.85rem 2rem',
          background: BG_SURFACE,
          borderTop: `1px solid ${BORDER_DEFAULT}`,
          borderBottomLeftRadius: 12,
          borderBottomRightRadius: 12,
          zIndex: 10,
        }}>
          {saveState === 'error' && (
            <div style={{ ...FONT_LABEL, background: ERROR_BG, color: ERROR_TEXT, border: `1px solid ${ERROR_BORDER}`, borderRadius: 6, padding: '0.35rem 0.75rem', marginBottom: '0.5rem' }}>
              {t.errorSave}
            </div>
          )}
          {deleteError && (
            <p style={{ ...FONT_LABEL, color: ERROR_TEXT, margin: '0 0 0.5rem' }}>
              {deleteError}
            </p>
          )}
          {Object.keys(errors).length > 0 && (
            <p style={{ ...FONT_SMALL, color: ERROR_TEXT, margin: '0 0 0.5rem' }}>
              {t.errorRequired}
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
            <div>
              {currentMode === 'edit' && (
                <button
                  onClick={() => setConfirmState({ confirmLabel: t.btnDelete })}
                  disabled={disabled}
                  style={{ ...BTN_DESTRUCTIVE_OUTLINE, ...FONT_SMALL, borderRadius: 8, padding: '0.5rem 1rem', cursor: disabled ? 'not-allowed' : 'pointer' }}
                >
                  {t.btnDelete}
                </button>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              {dirty && (
                <span style={{ ...FONT_LABEL, color: UNSAVED_AMBER }}>{t.unsavedLabel}</span>
              )}
              <button onClick={onCancel} disabled={disabled} style={btnSecondary}>{t.btnCancel}</button>
              <button onClick={handleSave} disabled={disabled} style={btnPrimary}>{saveState === 'saving' ? t.saving : saveState === 'ok' ? t.saveOk : t.btnSave}</button>
            </div>
          </div>
        </div>
      )}
      <SaveErrorDialog
        open={saveErrorOpen}
        lang={lang}
        onRetry={handleRetrySave}
        onDiscard={handleDiscardChanges}
      />
    </div>
  );
}
